import {
  createContext, FC, ReactNode, useCallback, useContext, useMemo, useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  addFavoriteNotification,
  removeFavoriteNotification,
  restoreFavoriteNotification,
  favoritesErrorNotification,
} from '../utils/favoriteNotifications';
import { FavoriteApiResponse } from '../types/api/v4/favorites';
import { apiRequest } from '../utils/apiFetch';
import { useUserContext } from './UserProvider';
import { PATHS } from '../constants';
import { trackEvent } from '../3rdParty/tracking';
import { ListingCardLocation } from '../types/common/ListingCard';
import { GA_EVENT_FAVORITES } from '../gaEvents';

const GA_EVENT_ACTION_ADD = 'favorite';
const GA_EVENT_ACTION_REMOVE = 'unfavorite';
const GA_EVENT_PARAM_HIGHLIGHTED = 'highlighted';

type FavoritesContext = {
  favorites: Set<string>;
  hasFavorite: (listingId: string) => boolean;
  toggleFavorite: (
    listingId: string, isHighlighted?: boolean, cardLocation?: ListingCardLocation
  ) => void;
  canEditFavorites: boolean;
  shareLink: string;
  userFirstName: string;
};

const initialContext: FavoritesContext = {
  favorites: new Set([]),
  hasFavorite: () => false,
  toggleFavorite: () => {},
  canEditFavorites: false,
  shareLink: '',
  userFirstName: '',
};

const favoritesContext = createContext<FavoritesContext>(initialContext);

type Props = {
  children: ReactNode;
  value: FavoriteApiResponse | null;
};

export const FavoritesProvider: FC<Props> = ({
  children,
  value,
}) => {
  const shareLink = value?.share_link ?? '';
  const userId = value?.user_id ?? '';
  const favoriteBoats = value?.favorite_boats ?? [];
  const userFirstName = value?.first_name ?? '';

  const [favorites, setFavorites] = useState(new Set(favoriteBoats));
  const { user } = useUserContext();
  const loggedInUserId = user?.id;
  const router = useRouter();

  const canEditFavorites = loggedInUserId === userId;

  const hasFavorite = useCallback((listingId: string) => favorites.has(listingId), [favorites]);

  const addFavorite = useCallback(
    async (listingId: string): Promise<void> => {
      await apiRequest('/api/v4/favorites/add_boat/', {
        method: 'POST',
        body: JSON.stringify({ boat_id: listingId }),
      });
    },
    [],
  );

  const removeFavorite = useCallback(async (listingId: string): Promise<void> => {
    await apiRequest('/api/v4/favorites/remove_boat/', {
      method: 'DELETE',
      body: JSON.stringify({ boat_id: listingId }),
    });
  }, []);

  const toggleFavorite = useCallback(
    async (listingId: string, isHighlighted: boolean, cardLocation: ListingCardLocation) => {
      const isFavorite = hasFavorite(listingId);
      const gaParams = {
        p1: listingId,
        p3: cardLocation,
      };
      const update = new Set(favorites);
      if (isFavorite) {
        await removeFavorite(listingId).then(() => {
          update.delete(listingId);
          setFavorites(update);
        });
        if (router.asPath === PATHS.FAVORITES && !router.query.favoritesId) {
          restoreFavoriteNotification(async () => {
            trackEvent(GA_EVENT_FAVORITES, {
              ...gaParams,
              p2: GA_EVENT_ACTION_ADD,
            });
            await addFavorite(listingId).then(() => {
              setFavorites(new Set(favorites).add(listingId));
            });
          });
        } else {
          removeFavoriteNotification();
        }
        trackEvent(GA_EVENT_FAVORITES, {
          ...gaParams,
          p2: GA_EVENT_ACTION_REMOVE,
          ...(isHighlighted && { p4: GA_EVENT_PARAM_HIGHLIGHTED }),
        });
      } else {
        await addFavorite(listingId).then(() => {
          update.add(listingId);
          setFavorites(update);
        });
        if (update.size > 0) {
          addFavoriteNotification();
          trackEvent(GA_EVENT_FAVORITES, {
            ...gaParams,
            p2: GA_EVENT_ACTION_ADD,
            ...(isHighlighted && { p4: GA_EVENT_PARAM_HIGHLIGHTED }),

          });
        } else {
          favoritesErrorNotification();
        }
      }
    },
    [favorites, hasFavorite, addFavorite, removeFavorite, router],
  );

  const contextValue = useMemo(
    () => ({
      favorites,
      hasFavorite,
      toggleFavorite,
      canEditFavorites,
      shareLink,
      userFirstName,
    }),
    [favorites, hasFavorite, toggleFavorite, canEditFavorites, shareLink, userFirstName],
  );

  return <favoritesContext.Provider value={contextValue}>{children}</favoritesContext.Provider>;
};

export const useFavoritesContext = (): FavoritesContext => {
  const context = useContext(favoritesContext);

  if (!context) {
    throw new Error('useFavoritesContext must be used within a FavoritesProvider');
  }

  return context;
};
